// TicketFileSurface — the ticket's ONE host of the viewing grammar (FileSurface,
// FRONTEND-PATTERNS §Files) in two modes: BROWSE, a tile of the peek's Evidence
// opened over the peek — the open file is `?file=` URL state the PANEL owns
// (one history step: pushed on open, a step replaces, and Escape or Back gives it
// back — `useUrlOverlay`) and the
// pager walks the ticket's media; REVIEW, the walk over the ticket's WAITING
// photos (the panel's "Review n photos" · R) — every answer advances, and the
// last one hands back (`onDone`: the peek closes the walk and moves on to the
// list walk's next row — stores/rowWalkStore). Both modes speak the library's answers and answer with its
// words; every answer invalidates the tickets prefix so the row follows the
// engine. Review-only at this phase (`canWrite` false — a bad photo is removed in
// the site's library; APP-380); notes stay open to every reader.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FLOW_PAGE_SIZE } from '@ems-app/schemas/files/vocabulary';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import type { FileTopic } from '@ems-app/schemas/files/vocabulary';
import type { TicketDetail } from '@ems-app/schemas/tickets/responses';
import { FileSurface } from '../../components/patterns/FileSurface.js';
import { displayName, shortFileName, useTopicLabel, type LibraryFile } from '../../components/patterns/filesDisplay.js';
import { ticketNumberText } from '../../components/patterns/ticketsDisplay.js';
import type { PickerTopic, WhatItShowsValue } from '../../components/patterns/WhatItShowsPicker.js';
import { useToast } from '../../components/ui/Toast.js';
import {
  downloadFile,
  fetchPreviewLink,
  useAddNote,
  useClassifyFile,
  useDeleteNote,
  useEditNote,
  useFileDetail,
  useFilesOptions,
  mediaQueryOptions,
  useMedia,
  useRestoreNote,
  useStageLink,
} from '../../hooks/useFiles.js';
import { useHeld } from '../../hooks/useHeld.js';
import { useDisplayZone } from '../../hooks/useDisplayZone.js';
import { useMe } from '../../hooks/useMe.js';
import { ticketsKeys } from '../../hooks/useTickets.js';
import { useRefusalToast } from '../../components/patterns/forms/useQuickSave.js';

export type TicketFileSurfaceProps = {
  projectId: string;
  ticket: Pick<TicketDetail, 'id' | 'number' | 'location' | 'evidence_count'>;
  /** The viewer holds `files:review` — the answer rows show; the walk is only ever opened by a reviewer. */
  canReview: boolean;
  onClose: () => void;
} & (
  | {
      mode: 'browse';
      /** The open file — the panel's `?file=` slot. */
      fileId: string;
      /** A step: the next id (the panel writes it with replace). */
      onOpen: (id: string) => void;
    }
  | {
      mode: 'review';
      /** The last waiting photo was answered (or none waited) — the host decides what follows. */
      onDone: () => void;
    }
);

export function TicketFileSurface(props: TicketFileSurfaceProps) {
  const { projectId, ticket, canReview, onClose } = props;
  // The surface's one zone (ADR-082 §2): the ticket's site's; the project's where the ticket stands at no site.
  const display = useDisplayZone({ kind: 'project', projectId });
  const timeZone = ticket.location.timezone_iana ?? display.zone ?? display.viewerZone;
  const review = props.mode === 'review';
  const { t } = useTranslation('files');
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useMe((s) => s.user.id).data ?? '';
  const topicLabel = useTopicLabel();
  const anchor = `ticket:${ticket.id}`;

  // The lens: the walk reads what still waits, oldest first (the visit's story in
  // order); a browse reads the ticket's media, newest first (the Evidence tiles'
  // order) — one flat page of the flow at a time (ADR-078 §4): the walk crosses
  // to the next page when the cursor steps past the page's last photo.
  const [page, setPage] = useState(1);
  const view = useMemo(() => ({ ...(review ? { review: true, sort: 'oldest' as const } : { review: false, sort: 'newest' as const }), group: null, page }), [review, page]);
  const question = useMemo(() => ({ anchor }), [anchor]);
  const media = useMedia(projectId, question, view);
  const options = useFilesOptions(projectId, anchor);
  const items = useMemo(() => media.data?.items ?? [], [media.data]);
  const reviewCount = media.data?.review_count ?? items.length;
  // The page's place in the stream — the answer's own frame — and whether another page follows.
  const offset = media.data?.frame.offset ?? 0;
  const morePages = media.data !== undefined && offset + items.length < media.data.total;
  // The answer on screen IS the page asked for — never the previous page's placeholder while the next one comes: the
  // walk pins a photo on a landed answer alone.
  const landed = media.data !== undefined && !media.isPlaceholderData && media.data.frame.offset === (page - 1) * FLOW_PAGE_SIZE;

  // The cursor. BROWSE: the panel's slot. REVIEW: the chosen photo — the first
  // waiting one until the person chooses, PINNED the moment it shows: the lens
  // re-reads itself in the background, and a colleague answering the photo on
  // screen must not swap it under a half-made answer. The walk moves on the
  // person's own act only (an answer, a step).
  const [chosenId, setChosenId] = useState<string | null>(null);
  if (review && chosenId === null && landed && items[0]) setChosenId(items[0].id);
  const openId = review ? chosenId : props.fileId;
  const index = openId ? items.findIndex((f) => f.id === openId) : -1;
  const onDone = review ? props.onDone : undefined;
  const doneRef = useRef(false);
  const finish = useCallback(() => {
    if (doneRef.current || !onDone) return;
    doneRef.current = true;
    onDone();
  }, [onDone]);
  // Nothing waits — hand back at once, once: when the walk OPENED on it (a stale
  // count). Never because a colleague emptied the lens meanwhile: that would close
  // the surface under a half-made answer and thank the person for a review they
  // did not do (the person's OWN last answer hands back in `answered`).
  const openedRef = useRef(false);
  const ownAnswerRef = useRef(false);
  useEffect(() => {
    if (!review || !media.isSuccess || !landed) return;
    const opening = !openedRef.current;
    openedRef.current = true;
    if (items.length === 0 && opening) finish();
  }, [review, media.isSuccess, landed, items.length, finish]);
  // A step across pages (or the person's own answer of a page's LAST photo) is an EVENT: the walk FETCHES the page it
  // moves into (the same query the hook reads — the cache hands it to the hook at once), then pins its first photo
  // stepping forward, its last stepping back, the photo that slid into the answered one's place after an answer (an
  // offset page: the lens re-reads, the next waiting photo takes the place). One crossing at a time.
  const crossing = useRef(false);
  const land = async (to: number, at: 'first' | 'last' | number): Promise<void> => {
    if (crossing.current) return;
    crossing.current = true;
    try {
      const answer = await qc.fetchQuery(mediaQueryOptions(projectId, question, { ...view, page: to }));
      const rows = answer.items;
      const target = at === 'first' ? rows[0] : at === 'last' ? rows[rows.length - 1] : rows[Math.min(at, rows.length - 1)];
      if (!target) {
        // An emptied page past the stream's end while photos still wait: back a page, on its last photo — never
        // "all reviewed" with the lens not empty.
        if (to > 1 && answer.total > 0) {
          crossing.current = false;
          return land(to - 1, 'last');
        }
        if (review && answer.total === 0 && ownAnswerRef.current) finish();
        return;
      }
      setPage(to);
      if (review) setChosenId(target.id);
      else props.onOpen(target.id);
    } finally {
      crossing.current = false;
    }
  };

  // The open photo is held while a re-read no longer carries it (deleted or
  // answered by someone else) — the pager goes, the photo stays.
  const wire = useHeld(index >= 0 ? items[index]! : null, openId);
  const detail = useFileDetail(projectId, wire?.id ?? null);
  const file = useMemo<LibraryFile | null>(() => {
    if (!wire) return null;
    const own = detail.data && detail.data.id === wire.id ? { notes: detail.data.notes, versions: [] } : {};
    return { ...wire, thumb: null, fresh: false, ...own };
  }, [wire, detail.data]);
  const stage = useStageLink(projectId, wire);

  // The picker's material — the ticket anchor's options: the kinds present at the ticket's site + every topic; the
  // units come from the unit lookup inside that site (ADR-078 §6).
  const siteId = ticket.location.kind === 'site' && ticket.location.id ? ticket.location.id : null;
  const kinds = useMemo(() => options.data?.kinds ?? [], [options.data]);
  const topicOptions = useMemo<PickerTopic[]>(() => (options.data?.all_topics ?? []).map((k) => ({ value: k, label: topicLabel(k) })), [options.data, topicLabel]);

  const classify = useClassifyFile(projectId);
  const addNote = useAddNote(projectId);
  const editNote = useEditNote(projectId);
  const deleteNote = useDeleteNote(projectId);
  const restoreNote = useRestoreNote(projectId);
  const [previousAnswer, setPreviousAnswer] = useState<{ label: string; value: WhatItShowsValue } | null>(null);
  const fail = useRefusalToast('ticket-file');

  const step = (delta: number) => {
    const at = index + delta;
    // Past the page's last photo the walk moves to the next page (its first photo); before its first, back a page.
    if (at >= items.length) {
      if (morePages) void land(page + 1, 'first');
      return;
    }
    if (at < 0) {
      if (page > 1) void land(page - 1, 'last');
      return;
    }
    const next = items[at];
    if (!next || next.id === openId) return;
    if (review) setChosenId(next.id);
    else props.onOpen(next.id);
  };
  // The totals are the server's, never the page: the wait count for the walk, the evidence count for a browse.
  const shown = media.data?.total ?? (review ? reviewCount : ticket.evidence_count);
  /** An answer: the tickets prefix refetches so the row's stage follows the engine;
   *  the WALK advances (the next item, the previous when the last one leaves; the
   *  last answer of all hands back) — a browse stays on the photo. */
  const answered = () => {
    void qc.invalidateQueries({ queryKey: ticketsKeys.all(projectId) });
    if (!review) return;
    ownAnswerRef.current = true;
    // The answer takes one off the wait — unless the photo had already left it (a colleague answered it first).
    if (reviewCount - (index < 0 ? 0 : 1) <= 0) {
      finish();
      return;
    }
    // (index < 0: the answered photo had already left the lens — on to the first one still waiting)
    const next = index < 0 ? (items[0] ?? null) : (items[index + 1] ?? null);
    if (next) setChosenId(next.id);
    // The page's LAST photo answered: the lens re-reads and the next waiting photo slides into its place (an offset
    // page) — the walk lands there, or on the page's new last photo, or back a page.
    else void land(page, Math.max(0, index));
  };

  if (!file) return null;
  const siteFiles = ticket.location.kind === 'site' && ticket.location.id ? `/p/${projectId}/sites/${ticket.location.id}/files` : null;
  const pager =
    shown > 1 && index >= 0
      ? { onPrev: () => step(-1), onNext: () => step(1), prevDisabled: index <= 0 && page === 1, nextDisabled: index >= items.length - 1 && !morePages, position: { index: offset + index + 1, total: shown } }
      : undefined;

  return (
    <FileSurface
      file={file}
      anchorLabel={`${ticketNumberText(ticket.number)} · ${ticket.location.name}`}
      timeZone={timeZone}
      mode={review ? 'review' : 'browse'}
      progress={review && index >= 0 ? { index: offset + index + 1, total: shown } : undefined}
      pager={pager}
      onStep={shown > 1 ? step : undefined}
      onClose={onClose}
      preview={stage.preview}
      onSourceError={() => stage.retry()}
      onSourceExpired={() => stage.refresh()}
      projectId={projectId}
      siteId={siteId}
      kinds={kinds}
      topicOptions={topicOptions}
      previousAnswer={previousAnswer}
      currentUserId={me}
      canWrite={false}
      canReview={canReview}
      canNote
      onConfirm={(f, s) =>
        classify.mutateAsync({ fileId: f.id, decision: 'confirm', assets: [s.asset_id], topics: [] }).then(() => {
          setPreviousAnswer({ label: s.label, value: { assets: [s.asset_id], topics: [] } });
          toast.success(t('toast.classified', { what: s.label }));
          answered();
        }, fail)
      }
      onReclassify={(f, next, names) => {
        const what = [...next.assets.map((id) => names.get(id) ?? id), ...next.topics.map(topicLabel)].join(' · ');
        classify.mutateAsync({ fileId: f.id, decision: 'change', assets: next.assets, topics: next.topics as FileTopic[] }).then(() => {
          setPreviousAnswer({ label: what, value: next });
          toast.success(t('toast.classified', { what }));
          answered();
        }, fail);
      }}
      onNothing={(f) =>
        classify.mutateAsync({ fileId: f.id, decision: 'nothing', assets: [], topics: [] }).then(() => {
          toast.success(t('toast.reviewed'));
          answered();
        }, fail)
      }
      onDownload={(f) => {
        toast.info(t('toast.downloading', { name: shortFileName(displayName(f)) }));
        downloadFile(projectId, f).catch(fail);
      }}
      onOpenInTab={(f) => fetchPreviewLink(projectId, f.id, 'original').then((l) => window.open(l.url, '_blank', 'noopener'), fail)}
      {...(siteFiles
        ? {
            // The site's library is the photo's home — the surface closes with the route.
            onOpenAtAnchor: (f: LibraryFile) => navigate(`${siteFiles}?file=${f.id}`),
            onCopyLink: (f: LibraryFile) => {
              void navigator.clipboard?.writeText(`${location.origin}${siteFiles}?file=${f.id}`).catch(() => {});
              toast.success(t('toast.linkCopied'));
            },
          }
        : {})}
      onAddNote={(f, text) => addNote.mutateAsync({ fileId: f.id, body: text }).then(() => toast.success(t('toast.noteAdded')))}
      onEditNote={(f, noteId, text) => editNote.mutateAsync({ fileId: f.id, noteId, body: text }).then(() => toast.success(t('toast.noteSaved')))}
      onDeleteNote={(f, noteId) =>
        deleteNote.mutateAsync({ fileId: f.id, noteId }).then(
          () => toast.success(t('toast.noteDeleted'), { action: { label: t('toast.undo'), onClick: () => restoreNote.mutateAsync({ fileId: f.id, noteId }).catch(fail) } }),
          fail,
        )
      }
    />
  );
}
